<template>
  <div class="signin-wrapper">
    <AuthWrapper
      :show-campaign-duplicate-connection="showCampaignDuplicateConnection"
      :inviting-entity-name="campaignEntityName"
      @closeDuplicateCampaign="closeCampaignDuplicateConnection"
    >
      <AuthHeader>
        <template #heading>
          <div class="heading">
            <div class="text" v-if="isResponder">{{ translate("sign_in.welcome_responder") }}</div>
            <div class="text" v-else>
              <!-- prettier-ignore -->
              <i18n-t keypath="sign_in.welcome_styled">
                <span><strong>gr<span class="alt-font-family">a</span>phite<span class="reg-mark">&reg;</span></strong>Connect</span>
              </i18n-t>
            </div>
          </div>
        </template>
        <template #subheader>
          <div class="sign-in-wrap">
            <template v-if="isResponder">
              {{ translate("sign_in.responder_need_account") }}
              <router-link :to="'/responder/signup'">{{ translate("sign_in.sign_up_now") }}</router-link>
            </template>
            <div class="pitch">{{ translate("sign_in.pitch") }}</div>
          </div>
        </template>
      </AuthHeader>
      <div class="form-wrap">
        <GraphiteAlert variant="info" :show="ssoEnabled">
          {{ translate("general.sso_enabled") }}
        </GraphiteAlert>
        <GraphiteAlert variant="info" :show="loggedOut">
          {{ translate("general.logged_out") }}
        </GraphiteAlert>
        <ResponderEmailVerificationAlert v-if="isResponder" />
        <form @submit="onSubmit" class="auth-form general-form">
          <div class="email-wrap">
            <QuestionInput
              :question-data="questions.email"
              v-model="form.email"
              :edit-mode="true"
              @updateValidState="validFields.email = $event"
            />
            <div class="remember-me">
              <GraphiteCheckbox v-model="rememberMe" @change="setRememberMe">
                {{ translate("sign_in.remember_me") }}
              </GraphiteCheckbox>
            </div>
          </div>
          <GraphiteAlert
            v-if="needsVerification && !ssoEnabled"
            class="verification-alert"
            :show="needsVerification"
            variant="info"
          >
            <template v-if="verifyResent">
              {{ translate("sign_up.validation_msg", [form.email]) }}
            </template>
            <i18n-t v-else keypath="sign_in.not_verified">
              <template #link>
                <div class="link-alike" @click="resendVerify({email: form.email, campaign})">
                  <LocalLoader v-if="sendingVerify" size="small" text="" :loading="sendingVerify" />
                  {{ translate("sign_in.resend") }}
                </div>
              </template>
            </i18n-t>
          </GraphiteAlert>
          <div v-if="(!ssoUrl || ssoAllowPassword) && !needsVerification" class="password-wrap">
            <QuestionInput
              :question-data="questions.password"
              v-model="form.password"
              :edit-mode="true"
              @updateValidState="validFields.password = $event"
            />
            <div class="forgot-wrap" v-if="!ssoEnabled && !isExcel">
              <router-link class="forgot-link" to="/forgot">{{ translate("forgot.link_text") }}</router-link>
            </div>
          </div>
          <div class="btn-wrap">
            <GraphiteAlert :show="!!authenticationError" :variant="authErrorVariant" class="auth-error">
              <template v-if="tooManyLogins">
                {{ $tc("sign_in.msg_too_many_logins", tooManyLoginsMinsToExpire) }}
              </template>
              <template v-else-if="notVerified">
                <template v-if="verifyResent">
                  {{ translate("sign_up.validation_msg", [form.email]) }}
                </template>
                <i18n-t v-else keypath="sign_in.not_verified">
                  <template #link>
                    <span class="link-alike" @click="resendVerify({email: form.email, campaign})">
                      {{ translate("sign_in.resend") }}
                    </span>
                  </template>
                </i18n-t>
              </template>
              <template v-else-if="!!customExcelError">{{ customExcelError }}</template>
              <template v-else>{{ translate("sign_in.msg_no_login") }}</template>
            </GraphiteAlert>
            <div class="mt-3" v-if="isExcel">
              <div class="excel-cta mb-3" v-html="excelCallToSignup" />
            </div>
            <div class="mt-3">
              <div>
                <IconButton type="submit" variant="primary" :disabled="!allValidCheck" :icon="['fal', 'lock']">
                  {{ translate("nav.sign_in") }}
                </IconButton>
              </div>
            </div>
            <div class="mt-3" v-if="ssoAllowPassword">
              <Note
                v-if="!!form.password"
                :title="translate('sign_in.sso_password_title')"
                :subTitle="translate('sign_in.sso_password_subtitle')"
                :iconVariant="NoteIconVariants.MESSAGE_EXCLAMATION"
                :variant="NoteVariants.PRIMARY"
              />
              <div>
                <IconButton
                  type="submit"
                  variant="primary"
                  class="extra-width"
                  :icon="['fal', 'lock']"
                  :disabled="!ssoEnabled || !validFields.email || !!form.password"
                >
                  {{ translate("nav.sign_in_sso") }}
                </IconButton>
              </div>
            </div>
            <div class="mt-3">
              <LegalText :actionText="translate('nav.sign_in')" class="mb-3" />
            </div>
          </div>
        </form>
      </div>
    </AuthWrapper>
  </div>
</template>

<script lang="ts" setup>
import AuthHeader from "@/components/auth/AuthHeader.vue";
import AuthWrapper from "@/components/auth/AuthWrapper.vue";
import LegalText from "@/components/auth/LegalText.vue";
import IconButton from "@/components/shared/IconButton.vue";
import LocalLoader from "@/components/shared/LocalLoader.vue";
import Note, {NoteIconVariants, NoteVariants} from "@/components/shared/Note.vue";
import {getStore} from "@/composables/get.store";
import {getCurrentRoute, getRouter} from "@/router/util";
import {ErrorCode} from "pg-isomorphic/enums/errors";
import debounce from "lodash/debounce";
import {isExcel} from "@/utils/excel";
import globalLogger from "../../logging";
import {QuestionType} from "pg-isomorphic/enums";
import {UrlUtility} from "pg-isomorphic/utils/url";
import jwtDecode from "jwt-decode";
import ResponderEmailVerificationAlert from "../responder/auth/ResponderEmailVerificationAlert.vue";
import {ResponderExcel} from "@/composables/responder/excel-commands";
import QuestionInput from "@/components/questions/QuestionInput.vue";
import type {ComputedRef, Ref} from "vue";
import {computed, defineProps, onBeforeUnmount, onMounted, provide, ref, toRefs, watch} from "vue";
import type {QuestionData} from "@/composables/questions/types";
import {getCurrentUserComputed, getUserActiveEntityId} from "@/composables/vuex";
import {translate} from "@/composables/i18n";
import type {AxiosError} from "axios";
import type {Campaign} from "@/composables/campaigns/types";
import type {JSONObject, JSONQuestion} from "pg-isomorphic/utils";
import {coerceFromArray} from "pg-isomorphic/utils";
import {ValidationType} from "pg-isomorphic/enums/question";
import type {SkeletonConnectionJWT} from "pg-isomorphic/api/connection";
import {all} from "ramda";
import {getValidationCheckFromQuestionData} from "@/composables/questions/validation";
import {QuestionInputAutoFocusKey} from "@/composables/questions/injections";
import {removeFromStorage, StorageKey} from "@/composables/local";

const logger = globalLogger.getLogger("SignIn");
const localStore = window.localStorage;
const REMEMBER_ME = "remember-me";

const props = defineProps({
  newEmail: {
    type: String,
  },
  invited: {
    type: String,
  },
  invitedContact: {
    type: String,
  },
  invite: {
    type: String,
  },
  campaign: {
    type: String,
  },
  isResponder: {
    type: Boolean,
  },
});

const user = getCurrentUserComputed();
const store = getStore();
const route = getCurrentRoute();
const router = getRouter();

// PROPS
const {newEmail, invitedContact, campaign, isResponder, invite} = toRefs(props);

// DATA
const form: Ref<{email: string; password: string}> = ref({email: newEmail.value, password: ""});
const validFields: Ref<{email: boolean; password: boolean}> = ref({email: !!newEmail.value, password: null});
const step: Ref<number> = ref(0);
const showBrowserIncompatibleModal: Ref<boolean> = ref(false);
const showCampaignDuplicateConnection: Ref<boolean> = ref(false);
const redirectUrl: Ref<string> = ref("");
const emailCheckDebounced: Ref<Function> = ref(() => {});
const customExcelError: Ref<string> = ref(null);
const autoFocusInput: Ref<string> = ref("email");
const isModified: Ref<boolean> = ref(false);
const rememberMe: Ref<boolean> = ref(!!localStore.getItem(REMEMBER_ME));
const sendingVerify: Ref<boolean> = ref(false);

// COMPUTED
const supportsHistoryApi: ComputedRef<boolean> = computed(() => {
  return !!(window.history && window.history.pushState);
});

const authenticationError: ComputedRef<string> = computed(() => store.state.authenticationError);
const signInError: ComputedRef<AxiosError<{code: string; messageParameters: JSONObject}>> = computed(
  () => store.state.signInError,
);
const isAuthenticated: ComputedRef<boolean> = computed(() => store.state.isAuthenticated);
const loading: ComputedRef<boolean> = computed(() => store.state.loading);
const requestedPath: ComputedRef<string> = computed(() => store.state.requestedPath);
const ssoUrl: ComputedRef<string> = computed(() => store.state.ssoUrl);
const ssoAllowPassword: ComputedRef<boolean> = computed(() => store.state.ssoAllowPassword);
const needsVerification: ComputedRef<boolean> = computed(() => store.state.needsVerification);

const verifyResent: ComputedRef<boolean> = computed(() => store.state.signUp.verifyResent);
const entityId: ComputedRef<string> = computed(() => store.state.signUp.entityId);
const campaignConnectionError: ComputedRef<AxiosError<{code: string}>> = computed(
  () => store.state.signUp.campaignConnectionError,
);
const campaignInfo: ComputedRef<Campaign> = computed(() => store.state.signUp.campaignInfo);
const email: ComputedRef<string> = computed(() => store.state.signUp?.email);
const inviteToken: ComputedRef<string> = computed(() => store.state.signUp.inviteToken);
const invitationInvalid: ComputedRef<boolean> = computed(() => store.state.signUp.invitationInvalid);

const excelCallToSignup: ComputedRef<string> = computed(() => {
  return translate("sign_up.excel_sign_up_cta", {
    contact_us: `<a target="_new" tabindex="7" title="${translate(
      "sign_up.excel_contact_us",
    )}" href="https://www.graphiteconnect.com/contact/">${translate("sign_up.excel_contact_us")}</a>`,
  });
});
const loggedOut: ComputedRef<boolean> = computed(() => {
  return !!route.query.auth;
});
const authErrorVariant: ComputedRef<string> = computed(() => {
  return verifyResent.value ? "info" : "danger";
});
const tooManyLogins: ComputedRef<boolean> = computed(() => {
  return signInError.value?.response?.data?.code === ErrorCode.TOO_MANY_LOGINS;
});
const notVerified: ComputedRef<boolean> = computed(() => {
  return signInError.value?.response?.data?.code === ErrorCode.USER_NOT_VERIFIED;
});
const campaignDuplicateConnection: ComputedRef<boolean> = computed(() => {
  return campaignConnectionError.value?.response?.data?.code === ErrorCode.DUPLICATE_CONNECTION;
});
const campaignEntityName: ComputedRef<string> = computed(() => {
  return campaignInfo.value?.entityDetails?.dbaName || "";
});
const tooManyLoginsMinsToExpire: ComputedRef<number> = computed(() => {
  const secondsToExpire = (signInError.value?.response?.data?.messageParameters?.secondsToExpire || 0) as number;
  return Math.ceil(secondsToExpire / 60);
});
const ssoEnabled: ComputedRef<boolean> = computed(() => {
  return ssoUrl.value?.length > 0;
});
const questions: ComputedRef<{[key: string]: QuestionData}> = computed(() => {
  return {
    email: {
      key: "email",
      label: translate("sign_up.biz_email"),
      required: false,
      editMode: false,
      type: QuestionType.TEXT,
      validation: {
        rules: {
          type: ValidationType.GRAPHITE_EMAIL,
          empty: false,
          optional: false,
        },
      },
    },
    password: {
      key: "password",
      label: translate("sign_up.password"),
      editMode: true,
      required: false,
      password: true,
      type: QuestionType.TEXT,
      validation: {
        rules: {
          type: ValidationType.STRING,
          optional: false,
        },
      },
    },
  };
});

const allValidCheck: ComputedRef<boolean> = computed(() => {
  return (
    all((key) => validFields.value[key] !== false, Object.keys(validFields.value)) &&
    (!!form.value.password || ssoEnabled.value) &&
    isModified.value
  );
});

// METHODS
const onSubmit = async (evt) => {
  evt.preventDefault();
  isModified.value = false;
  if (ssoUrl.value && !(form.value?.password && ssoAllowPassword.value)) {
    if (isExcel) {
      store.commit("authenticationError", false);

      let ssoUrlTemp = ssoUrl.value
        ?.replace("http://localhost:8080", "https://tunnel.cameron.projectgraphite.com")
        .replace(/https:\/\//, "https://excel-") // we can only access URLs that are whitelisted, so need to make it be the excel URL
        .replace(/https:\/\/(?:excel-)+/, "https://excel-"); // dedupe, just in case...
      ssoUrlTemp += "?RelayState=excel";
      try {
        await ResponderExcel.openSsoDialog(ssoUrlTemp);
      } catch (err) {
        console.error(err);
        customExcelError.value = err?.message || "Unknown Error";
        store.commit("authenticationError", true);
        // this.$store.commit('authErrorVariant', 'danger');
      }
    } else {
      window.location.href = ssoUrl.value + "?RelayState=app";
    }
    return;
  }
  await store.dispatch("signIn", form.value);
  if (isAuthenticated.value) {
    logger.trace(() => `signin onSubmit authenticated ${invitedContact.value}`, user.value, entityId.value);
    if (invitedContact.value) {
      await store.commit("signUp/accessRequestError", null);
      await store.dispatch("signUp/getTaxQuestions", {connection: user.value?.invitationDetails?.connection});
      await store.dispatch("signUp/getExistingEntityDetails", {
        user: user.value,
        entityId: user.value?.invitationDetails?.entity || entityId.value,
      });
      await router.push("/signup/existing");
    } else if (campaign.value) {
      // campaign sign in, user has existing company - link them
      await store.dispatch("signUp/createCampaignConnection", {entityId: user.value?.activeEntityId});
      if (!campaignConnectionError.value) {
        await router.push("/signup/next");
      } else if (campaignDuplicateConnection.value) {
        showCampaignDuplicateConnection.value = true;
      } else {
        await router.push("/dash");
      }
    } else {
      if (requestedPath.value) {
        const reqPath = requestedPath.value;
        await store.dispatch("setRequestedPath", "");
        await router.push(reqPath);
      } else if (redirectUrl.value) {
        // for external redirect to www-site
        window.location.href = redirectUrl.value;
      } else {
        if (isResponder.value) {
          await router.push("/responder");
        } else {
          await router.push("/");
        }
      }
    }
  }
};

const showInvitationInvalid = async () => {
  await router.push(`/invalid_invitation`);
};

const reset = () => {
  form.value = {
    email: "",
    password: "",
  };
  validFields.value = {
    email: false,
    password: null,
  };
  step.value = 0;
  store.commit("authenticationError", false);
};

const loadParams = async (params) => {
  logger.trace(() => `loadParams: ${JSON.stringify(params)}`);

  if (params.inviteToken) {
    logger.trace(() => `saving inviteToken data: ${params.inviteToken}`);
    store.commit("signUp/inviteToken", params.inviteToken);
    const inviteInfo: SkeletonConnectionJWT = jwtDecode(String(params.inviteToken));
    logger.trace(() => `inviteInfo: ${JSON.stringify(inviteInfo)}`);

    store.commit("signUp/companyName", params.newName);
    store.commit("signUp/invitingEntityName", params.entityName);
    store.commit("signUp/invitingEntity", inviteInfo.requestingEntityId);
    store.commit("signUp/invitingUserName", params.sender);

    form.value.email = params.email;
    validFields.value.email = true; // assume it's valid coming from the invite

    await store.dispatch("signUp/checkInvitationEntity", {
      token: params.inviteToken,
    });
    if (invitationInvalid.value) {
      await showInvitationInvalid();
    }

    if (params.email) {
      await store.commit("signUp/email", params.email);
      await store.dispatch("signUp/checkEmail", {
        token: params.inviteToken,
      });
    }
  }
};

const closeCampaignDuplicateConnection = () => {
  showCampaignDuplicateConnection.value = false;
  router.push("/dash");
};

const resendVerify = (params: {email: string; campaign: string}) => {
  // resendVerify({email: form.email, campaign})
  sendingVerify.value = true;
  store.dispatch("signUp/resendVerify", params);
};

const setRememberMe = (val) => {
  if (validFields.value?.email && val) {
    localStore.setItem(REMEMBER_ME, form.value.email);
  } else if (!val) {
    localStore.removeItem(REMEMBER_ME);
  }
};

// for some reason, this has to run before mounted
if (form.value.email) {
  autoFocusInput.value = "password";
}

// LIFECYCLE
onMounted(async () => {
  // don't auto redirect if they are confirming email for responder
  if (isAuthenticated.value && !loading.value && !(isResponder.value && route.query.responderEmailConfirmation)) {
    if (requestedPath.value) {
      router.push(requestedPath.value);
    } else {
      router.push("/");
    }
  } else if (route.query?.redirect) {
    const redirect = coerceFromArray(route.query.redirect);
    if (redirect.match(/^(http|https)/)) {
      redirectUrl.value = redirect;
      logger.trace(`set redirect: ${redirectUrl.value}`);
    } else {
      logger.warn(`redirect url is not valid: ${route.query.redirect}`);
    }
  }

  // we don't need to keep trying to reconnect on the login screen
  store.commit("alwaysReconnect", false);

  // Check for browser compatibility
  if (!supportsHistoryApi.value) {
    showBrowserIncompatibleModal.value = true;
  }
  const validationCheck = getValidationCheckFromQuestionData(questions.value?.email as JSONQuestion);
  emailCheckDebounced.value = debounce((email) => {
    const isEmail = validationCheck(email, true);
    if (isEmail.passes === true) {
      store.dispatch("checkSSO", {email});
    } else {
      store.dispatch("resetSSO");
    }
  }, 200);

  if (invite.value) {
    await store.commit("signUp/invite", invite.value);
    const params = UrlUtility.decode(invite.value);
    // ugly but works
    setTimeout(async () => {
      await loadParams(params);
    }, 300);

    // store in case we need later
    await store.dispatch("signUp/storeInvite", params);
  } else {
    const params = await store.dispatch("signUp/getInvite");

    if (params) {
      // ugly but works
      setTimeout(async () => {
        await loadParams(params);
      }, 300);
    }
  }

  if (localStore.getItem(REMEMBER_ME) && !newEmail.value) {
    form.value.email = localStore.getItem(REMEMBER_ME);
    validFields.value.email = true;
  }

  removeFromStorage(`${StorageKey.CONTACT_OPTIONS}_${getUserActiveEntityId()}`);
});

// WATCHES
watch(
  () => form?.value?.email,
  (email) => {
    store.commit("needsVerification", false);
    store.commit("authenticationError", false);
    store.commit("needsVerification", false);
    store.commit("signUp/verifyResent", false);
    isModified.value = true;
    emailCheckDebounced.value(email);

    if (rememberMe.value && validFields.value?.email) {
      localStore.setItem(REMEMBER_ME, form.value?.email);
    } else if (!rememberMe.value && localStore.getItem(REMEMBER_ME)) {
      localStore.removeItem(REMEMBER_ME);
    }
  },
);

watch(
  () => form?.value?.password,
  () => {
    isModified.value = true;
    store.commit("authenticationError", false);
  },
);

// PROVIDES
provide(QuestionInputAutoFocusKey, autoFocusInput);

onBeforeUnmount(() => {
  reset();
  store.commit("alwaysReconnect", true);
});

defineOptions({
  compatConfig: {
    MODE: 3,
  },
});
</script>

<style lang="less" scoped>
@import "@{globals}";

.excel {
  max-width: 100%;

  .row {
    margin-right: 0;
    margin-left: 0;
  }
}

.heading {
  .text {
    font-size: @font30;

    .reg-mark {
      font-size: @font8;
      vertical-align: super;
    }
  }
  .pitch {
    color: @almostBlack;
    font-weight: 500;
    font-size: @font16;
  }
}

.form-wrap {
  .excel & {
    margin: 30px;
  }

  .general-form {
    > div {
      margin: 20px 0;
    }

    .verification-alert {
      font-size: @font14;

      span {
        display: flex;
        justify-content: space-between;

        div.link-alike {
          display: flex;
          gap: 4px;
        }
      }
    }

    .auth-error {
      .link-alike {
        color: inherit;
        text-decoration: underline;
      }
    }

    .remember-me {
      font-size: @font13;
      font-weight: 400;
      padding-top: 4px;
    }

    .forgot-wrap {
      padding-top: 4px;
      text-align: right;

      .forgot-link {
        margin-top: 8px;
        font-size: @font16;
        font-weight: 600;
      }
    }
  }

  .btn-wrap {
    margin-top: 25px;
  }

  .btn-icon {
    margin-right: 10px;
  }
}

.excel-cta {
  color: #70747f;
  font-size: @font14;
  line-height: 18px;
}
</style>

<style lang="less">
@import "@{globals}";
.auth-wrapper {
  .right-side {
    &.auth-wrapper-wrapped {
      .heading {
        .text {
          font-size: @font20;

          .reg-mark {
            font-size: @font6;
          }
        }

        .pitch {
          font-size: @font12;
        }
      }
    }
  }
}

.form-wrap {
  .general-form {
    .remember-me {
      .custom-checkbox {
        .custom-control-label {
          padding-top: 2px;
        }
      }
    }
  }
}
</style>
